import type { MeResponse } from '@palaccount/contracts';
import { LogOut } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useRailTip } from '@/app/shell/rail-tip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSignOut } from '@/lib/session';
import { useUiStore } from '@/lib/ui-store';
import { useMediaQuery } from '@/lib/use-viewport';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase() || '?';
}

/**
 * The side-menu footer's user card (§4.3.1): round initials avatar on a
 * blue-to-violet gradient, name, role line, sign-out icon at the trailing
 * edge. GEN-6 keeps it fixed below a hairline; GEN-7 makes the icon end the
 * session; GEN-8 makes the card itself open a small menu with "My
 * preferences" and "Sign out".
 *
 * ON THE RAIL (sidebar.tsx, Oct 2026): `expanded` false draws the avatar
 * alone, centred in the same 56px cell every row's icon sits in; the name and
 * role fade and slide in with the labels when the rail opens, and the
 * sign-out button is MOUNTED only while it is open — a control a person
 * cannot see must not be a Tab stop, and GEN-7's act is still one keystroke
 * away through the card's own menu. A keyboard user landing on the closed
 * card gets the name in the rail's bubble. `onMenuOpenChange` lets the rail
 * stay open while the menu (a portal outside it) is up.
 *
 * The role line: /api/me carries the owner flag (R18) but no role name yet,
 * so the card shows Owner / User until the role arrives on the session read.
 */
export function UserCard({
  me,
  expanded = true,
  onMenuOpenChange,
}: {
  me: MeResponse;
  expanded?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const tip = useRailTip();
  const rtl = useUiStore((state) => state.locale === 'ar');
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const spring = reduced ? { duration: 0 } : { type: 'spring' as const, stiffness: 420, damping: 38 };

  const handleSignOut = () => {
    signOut.mutate(undefined, {
      // GEN-7: end the session, then open GEN-S1.
      onSettled: () => void navigate('/sign-in', { replace: true }),
    });
  };

  return (
    <div className="flex items-center border-t border-border px-2.5 py-2">
      <DropdownMenu onOpenChange={(open) => onMenuOpenChange?.(open)}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('shell.userMenu')}
            onFocus={(event) => tip?.onFocus(me.user.name, event.currentTarget)}
            onBlur={tip?.onBlur}
            className="flex h-11 min-w-0 flex-1 items-center rounded-lg text-start outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            <span aria-hidden className="flex w-14 shrink-0 items-center justify-center">
              <span className="flex size-[34px] items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-violet-600 text-xs font-semibold text-white">
                {initialsOf(me.user.name)}
              </span>
            </span>
            <motion.span
              className="min-w-0 flex-1 pe-2"
              initial={false}
              animate={{ opacity: expanded ? 1 : 0, x: expanded ? 0 : rtl ? 12 : -12 }}
              transition={spring}
            >
              <span className="block truncate text-sm font-bold leading-tight">{me.user.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {me.user.isOwner ? t('shell.owner') : t('shell.member')}
              </span>
            </motion.span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-52">
          <DropdownMenuItem onSelect={() => void navigate('/settings')}>
            {t('shell.myPrefs')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={handleSignOut}>{t('shell.signOut')}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.button
            key="sign-out"
            type="button"
            onClick={handleSignOut}
            disabled={signOut.isPending}
            aria-label={t('shell.signOut')}
            title={t('shell.signOut')}
            className="ms-1 flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={spring}
          >
            <LogOut className="size-4 rtl:rotate-180" />
          </motion.button>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
